import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthSuccess from '../components/AuthSuccess/AuthSuccess';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { TFunction } from 'i18next';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import { PASSWORD_REGEXP } from '../const';
import { FormProvider, useForm } from 'react-hook-form';
import { useUpdatePassword } from '../hooks/user/auth';
import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { TextStyles } from '@payler/ui-theme';
import { PasswordInputField } from '@payler/ui-components';
import { ApiErrorText } from '../components/ApiErrorText/ApiErrorText';
import { useGetAxiosError } from '../hooks/use-get-axios-error';

export const PasswordUpdatePage = () => {
  const [newPasswordSuccess, setNewPassword] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(['titles', 'auth']);

  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === 'true';

  const handleSetNewPasswordSuccess = useCallback(() => {
    setNewPassword(true);
  }, [setNewPassword]);
  const handleSuccessSubmitClick = useCallback(() => {
    navigate('/');
  }, [navigate]);
  return (
    <>
      <Helmet title={t('titles:recovery')} />
      {newPasswordSuccess ? (
        <AuthSuccess
          onSubmitSuccess={handleSuccessSubmitClick}
          title={t('auth:setNewPasswordSuccess.header')}
          text={t('auth:setNewPasswordSuccess.descriptionLong')}
          submitText={t('auth:setNewPasswordSuccess.continueButton')}
        />
      ) : (
        <SetNewPasswordForm
          onSubmitSuccess={handleSetNewPasswordSuccess}
          expired={expired}
        />
      )}
    </>
  );
};

export type TSetNewPasswordFormProps = {
  onSubmitSuccess: () => void;
  expired?: boolean;
};

interface FormData {
  password: string;
  confirmPassword: string;
}

function createResolver(t: TFunction) {
  return yupResolver(
    yup.object().shape({
      password: yup
        .string()
        .required(t('validate.required'))
        .min(12, t('validate.minPasswordLengthReset'))
        .max(30, t('validate.maxPasswordLength'))
        .matches(PASSWORD_REGEXP, t('validate.notMatchPasswordPattern')),
      confirmPassword: yup
        .string()
        .required(t('validate.required'))
        .test((value, ctx) => {
          if (!value)
            return ctx.createError({ message: t('validate.required') });
          if (value !== ctx.parent.password)
            return ctx.createError({
              message: t('validate.doNotMatchPassword')
            });
          if (!PASSWORD_REGEXP.test(value)) {
            return ctx.createError({
              message: t('validate.notMatchPasswordPattern')
            });
          }
          return true;
        })
    })
  );
}

export const SetNewPasswordForm: React.FC<TSetNewPasswordFormProps> = ({ onSubmitSuccess, expired}) => {
  const { t } = useTranslation(['auth']);
  const { t: tWithoutNs } = useTranslation();

  const methods = useForm<FormData>({
    resolver: createResolver(tWithoutNs),
    mode: 'onChange',
  });
  const password = methods.watch('password');
  const { getValues, trigger } = methods;
  useEffect(() => {
    if (getValues('confirmPassword')) void trigger('confirmPassword');
  }, [password, getValues, trigger]);
  const { handleSubmit, ...rest } = methods;

  const [serverErrorMessage, setServerErrorMessage] = useState<string>('');
  const getError = useGetAxiosError();

  const { mutate, isPending: isLoading } = useUpdatePassword();

  const onSubmit = ({ password }: FormData) => {
    setServerErrorMessage('');
    mutate(password, {
      onSuccess: onSubmitSuccess,
      onError: (err: any) => {
        const errorMessage = getError(err);
        const lowerMessage = errorMessage.toLowerCase();
        if (lowerMessage.includes('прежний пароль') || lowerMessage.includes('придумайте новый')) {
          setServerErrorMessage(t('auth:setNewPassword.passwordAlreadyUsed'));
        } else {
          setServerErrorMessage(errorMessage);
        }
      },
    });
  };

  return (
    <Box mt={{ base: 10, sm: 16 }} w="100%">
      <FormProvider {...methods}>
        <Box
          as="form"
          onSubmit={handleSubmit(onSubmit)}
          height="100%"
          position="relative"
        >
          <Text mb={1} as="h1" textStyle={TextStyles.h1}>
            {expired
              ? t('auth:setNewPassword.expiredHeader')
              : t('auth:setNewPassword.header')}
          </Text>
          {expired && <Text
            textStyle={TextStyles.BodyUI16Regular}
            color="primary.400"
          >
            {t('auth:setNewPassword.expiredDescription')}
          </Text>}
          <VStack mt={5} alignItems="stretch" spacing={3}>
            <PasswordInputField
              name="password"
              placeholder={t('auth:setNewPassword.newPassword')}
            />
            <PasswordInputField
              name="confirmPassword"
              placeholder={t('auth:setNewPassword.confirmPassword')}
            />

            {serverErrorMessage && (
              <ApiErrorText>{serverErrorMessage}</ApiErrorText>
            )}

            <Box
              display="flex"
              mt={5}
              w="100%"
              position={{ base: 'absolute', sm: 'initial' }}
              left="0"
              right="0"
              bottom={1}
            >
              <Button
                isLoading={isLoading}
                isDisabled={isLoading}
                type="submit"
                variant="primary"
                w="100%"
              >
                {t('auth:setNewPassword.submitButton')}
              </Button>
            </Box>
          </VStack>
        </Box>
      </FormProvider>
    </Box>
  );
};

